/**
 * A record of who did what, and when.
 *
 * WHAT THIS CAN AND CANNOT PROMISE
 *
 * REWRITTEN 30 AUGUST 2026, BECAUSE IT HAD STOPPED BEING TRUE. What follows
 * described a board with no server, and 0068 gave it one — nine triggers, all
 * `security definer`, writing sessions, areas, sprints, board roles, people,
 * teams and team membership as the rows change. The old text survived the
 * change it was about by a day, on the one screen whose job is to say what it
 * can prove. Vinnie found it at the moment the demonstration worked: a line
 * appeared that no browser had written, directly under a note saying that
 * could not happen.
 *
 * SO THERE ARE TWO HALVES NOW, and the difference is the whole of what this
 * can promise:
 *
 *   · WRITTEN BY THE DATABASE, and therefore unskippable. A trigger fires
 *     whether the change came from this app, a script, a command line or an
 *     assistant writing straight to Postgres. Nobody can decline it, because
 *     nobody is asked.
 *   · WRITTEN BY THE BROWSER, and therefore only as good as the browser.
 *     Whether a sign-in was fresh or a heartbeat — indistinguishable from the
 *     database's side, so the app has to say which. And the two things that
 *     change no row at all: the whole board downloaded as a file, and the
 *     approved copy of a document. Neither can be seen by a trigger, and both
 *     stay client-written until the export itself is made on the server.
 *
 * UNCHANGED, AND ENFORCED BY THE RULES RATHER THAN BY GOOD MANNERS: you may
 * only write an entry about yourself, and entries can never be changed or
 * deleted by anyone, admins included. `audit` carries a select policy and an
 * insert policy and no others, so there is no route to an update or a delete.
 *
 * WHAT IS STILL OUT OF REACH: a sign-in that was REFUSED. Writing needs a
 * token and somebody turned away has none. That one does need a server.
 */
import { currentEnv } from './env';
import type { Activity, Issue, ProjectMeta } from './types';
import { STATUS_LABELS } from './types';

/**
 * A short, unique id for one entry.
 *
 * Not for security — only so two entries written in the same millisecond are
 * still two entries.
 */
function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export type AuditKind =
  | 'signin'
  | 'invite'
  | 'role'
  | 'access'
  | 'project'
  | 'issue'
  | 'comment'
  | 'refused'
  /**
   * SOMETHING FLIGHTDECK DID, rather than somebody in this workspace.
   *
   * Written by the edge functions in six places — a support visit starting, a
   * person added from the operator portal, a password reissued. It has existed
   * since support visits did, and was missing from this union the whole time,
   * so every one of those lines rendered with a blank badge while every other
   * entry carried one. Vinnie spotted it on live on 17 August: "Ops FlightDeck
   * added orbit-cli@vsnlweb.com to this workspace as a member does not show the
   * Access Change badge like everything else."
   *
   * KEPT SEPARATE FROM `access` RATHER THAN FOLDED INTO IT. Both are changes to
   * who may do what, and labelling ours "Access change" would put the thing we
   * did in the same column as the things they did. A customer scanning their
   * own trail should be able to see at a glance which entries were us, and the
   * badge is the part that is scannable — the sentence beside it is not.
   */
  | 'support'
  /**
   * A SESSION ENDING, WHICH THE TRAIL COULD NOT SAY BEFORE — #305.
   *
   * `signin` had no counterpart. A trail that records arriving and never
   * leaving cannot answer the first question anybody asks of one: not "was
   * somebody here", but "how long were they here". Every sign-in on this board
   * before 29 August 2026 is open-ended and always will be — nothing can be
   * written for a session that ended before there was a word for it.
   *
   * ONE KIND FOR FOUR EVENTS, because they are one event with four causes: you
   * pressed Sign out, you signed out every browser at once, you ended a session
   * from the Devices list, or it aged out underneath you. The badge says a
   * session ended and the sentence says which — the same division `issue`
   * already makes between deleting, restoring and changing in bulk.
   *
   * NOT FOLDED INTO `signin` AS "the signin kind, negatively". A reader
   * scanning a column of badges for a session that never closed is scanning for
   * the ABSENCE of something, and absence is the one thing a badge cannot show.
   */
  | 'session'
  /**
   * A WORKSPACE SETTING CHANGED BY AN ADMIN — the currency, so far (0117).
   *
   * Its own kind rather than `access` or `project`: it is neither a change to
   * who may do what, nor a change to one project — it is a workspace-wide
   * preference an admin set, and the badge should say so plainly. Workspace-
   * scoped, so it never stamps a project name (WORKSPACE_KINDS below).
   */
  | 'settings';

export interface AuditEntry {
  id: string;
  at: number;
  who: string; // email — always the person who did it
  kind: AuditKind;
  what: string; // plain English, e.g. "signed in" or "made Pat Moore a project manager"
  project?: string; // which project it concerned, where that makes sense
  /**
   * THE WHOLE ISSUE, AS IT WAS, WHEN ONE IS DELETED.
   *
   * Deleting removed the document and nothing else. Not a line in this trail,
   * not a copy anywhere — and the issue's own history went with it, so there
   * was no way afterwards to know it had ever existed, let alone what it said
   * or who removed it. Of everything the board does, that was the only act
   * with no record of any kind.
   *
   * Kept here rather than in a bin of its own because this collection is
   * already append-only and unchangeable by anyone, including admins. A record
   * of a deletion that the deleter could then delete would be worth nothing.
   */
  snapshot?: string;
  /** Which project it came out of, so it can be put back in the right one. */
  projectId?: string;
  /** The structured facts behind the sentence, shown when the entry is opened. */
  detail?: AuditDetail;
}

/**
 * The structured facts behind a History line, surfaced when an entry is opened
 * — the drill. Written by the same trigger that writes the sentence and never
 * edited after, so it is as append-only as the line it sits beside. A sign-in
 * carries the device, system, browser and method it was made on; a content
 * change (filled by a later migration) carries what was touched and how it
 * moved. Every field is optional — a line shows only the parts it has.
 */
export interface AuditDetail {
  // A sign-in event.
  deviceKind?: string;
  os?: string;
  browser?: string;
  method?: string;
  environment?: string;
  newDevice?: boolean;
  // A content change — target, field, and the move from one value to another.
  targetType?: string;
  targetId?: string;
  targetTitle?: string;
  field?: string;
  from?: string;
  to?: string;
}

/**
 * WHERE A SIGN-IN CAME FROM — the admin-only network origin, migration 0150.
 *
 * Kept apart from `AuditDetail` on purpose: the device a sign-in reports is the
 * signer's to see, but the IP and the place it resolves to are for an
 * administrator investigating access and are "never shown to the person who
 * signed in". They arrive on their own collection (`auditOrigin`), which RLS
 * hands to admins alone, and the app merges each onto its sign-in line by the
 * `auditId` it points at. A non-admin simply receives none, and the drill shows
 * no origin block at all.
 */
export interface AuditOrigin {
  auditId: string;
  ip: string;
  city: string;
  country: string;
  at: number;
}
// `originPlace(o)` — the place an origin resolves to, as one line — lives in
// src/history.ts beside the other trail-rendering helpers, so this module (which
// reaches env and the rest of the model) stays off the trail-reading path that
// the reader and its tests load on their own.

/**
 * WHAT CHANGED ABOUT A PROJECT, IN WORDS.
 *
 * The trail held sign-ins and access changes and nothing about a project's own
 * settings. On 5 August 2026 PhotoSyncApp went from "fed from a tracker" to
 * "kept on the board" — which decides whether the board offers to clear a
 * blocker at all, because a tracker would push it straight back. Nobody meant
 * to do it, the button appeared where it should not have, five blockers were
 * cleared through it, and there was NO WAY TO FIND OUT WHAT CHANGED IT.
 *
 * TWO FIELDS ARE DELIBERATELY NOT RECORDED. `nextNumber` moves every time
 * anybody raises an issue, and `setUpAt` is a marker rather than a setting; a
 * trail carrying either would bury the changes worth reading under noise, which
 * is the same as not having one.
 */
const SETTING_LABEL: Partial<Record<keyof ProjectMeta, string>> = {
  name: 'the project name',
  key: 'the issue prefix',
  repoUrl: 'the code repository',
  projectOwnerName: 'the project owner',
  projectOwnerPosition: "the project owner's position",
  // The address a signing link is sent to — as governance-relevant as the name
  // and position it sits with, and recording those two while leaving this out
  // made the coverage asymmetric (a witness found it, 10 Oct 2026).
  projectOwnerEmail: "the project owner's email",
  // The trail's sole writer for this is the trigger (migration 0166), which
  // renders the stored 'YYYY-MM-DD' as "DD Mon YYYY" via fd_said_date (0173) —
  // the same "Sep" shape the phase (0163) and milestone (0164) lines use, so
  // every date in the record reads alike. This spec is exercised only by
  // test-recovery.ts, which does not move requestedOn, so it is left on said().
  requestedOn: 'the date this work was asked for',
  signatories: 'who signs work off here',
  // Not "where issues come from" — the sentence below is "changed X from A to
  // B", and that label made it read "come from from tracker to board".
  source: 'the source of issues',
  sourceName: 'the name of that source',
  doneChecks: 'the definition of done',
  keptByAssistant: 'whether an assistant keeps this board',
  assistantMayAnswer: 'whether an assistant may answer questions',
  assistants: 'which assistants work here',
  // Turning this off hides a line that was there yesterday, on every release at
  // once. Nothing is deleted — the devices stay in the record and come back with
  // the switch — but "where did the phones go" deserves an answer that is not a
  // guess. Set through setMeta rather than its own action, precisely so it lands
  // here; `assistantVersions` has its own action and so records nothing, which
  // is a gap worth closing separately rather than copying.
  tracksDevices: 'whether this project ships to devices',
};

/**
 * The two words `source` can hold, said so anybody can act on them.
 *
 * "changed the source of issues from tracker to board" is only meaningful to
 * somebody who already knows what those two words do — and the whole reason
 * this record exists is that the setting has consequences most people would not
 * guess.
 */
const SOURCE_SAID: Record<string, string> = {
  board: 'this board',
  tracker: 'an external tracker',
};

/** How a value reads in the trail. Lists become their items; empty says so. */
function said(v: unknown): string {
  /* A list of PEOPLE, not of strings. `join` on objects gives
     "[object Object], [object Object]" — a trail entry that records that
     something changed while hiding what, which is worse than no entry.

     An entry with no name is not a signatory yet: a signatory is filled in a
     field at a time, and between saves the list holds a nameless row. Skipping
     those — so a list with no named person reads "nothing" like an empty one —
     keeps the half-filled saves from writing "from nothing to " with nothing
     after it (and the database's fd_said_people, migration 0169, does the same). */
  if (Array.isArray(v) && v.some((x) => x && typeof x === 'object')) {
    const named = (v as { name?: string; position?: string }[])
      .filter((x) => x && (x.name ?? '') !== '')
      .map((who) => (who.position ? `${who.name} (${who.position})` : String(who.name)));
    return named.length ? named.join(', ') : 'nothing';
  }
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'nothing';
  if (v === undefined || v === null || v === '') return 'nothing';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  /*
   * A ROSTER KEYED BY NAME, NOT A LIST — the assistants map (ProjectMeta.
   * assistants is Record<name, entry>). Without this it fell past every guard
   * above to String(v) and wrote "[object Object]" into a trail that can never
   * be corrected; worse, two different maps stringify the same, so the only
   * line ever recorded was "from nothing to [object Object]" on the first
   * assistant and silence on every change after. Rendered like the people list
   * above — the members' names, sorted so the browser and the database trigger
   * that mirrors this (migration 0166) always read the same — so "which
   * assistants work here" names the assistants.
   */
  if (typeof v === 'object')
    return (
      Object.entries(v as Record<string, { name?: string }>)
        .map(([key, e]) => (e && e.name ? e.name : key))
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .join(', ') || 'nothing'
    );
  return SOURCE_SAID[String(v)] ?? String(v);
}

/**
 * One line per setting that actually moved, naming both halves.
 *
 * Both halves matter more here than elsewhere: "changed where issues come from"
 * does not tell you whether the board now owns its issues or an external file
 * does, and that difference is the one that caused the trouble.
 */
export function describeSettings(
  before: Partial<ProjectMeta>,
  patch: Partial<ProjectMeta>
): string[] {
  const out: string[] = [];
  for (const [key, label] of Object.entries(SETTING_LABEL) as [keyof ProjectMeta, string][]) {
    if (!(key in patch)) continue;
    const from = said(before[key]);
    const to = said(patch[key]);
    if (from === to) continue;
    out.push(`changed ${label} from ${from} to ${to}`);
  }
  return out;
}

/**
 * A PLAIN-ENGLISH LINE PER FIELD THAT ACTUALLY MOVED.
 *
 * Lives here, beside the rest of the record-keeping, rather than in the store.
 * It was in the store, which reaches Firebase — so checking it meant starting
 * the whole app, and the only other way to prove it was to clear a real
 * blocker and read the result back. That is precisely the destructive act this
 * record exists to soften. Nothing here touches the network.
 *
 * Long text fields report THAT they changed rather than printing both versions
 * into the sentence — but they keep the old wording alongside it, in `was`.
 */
const TRACKED: { key: keyof Issue; label: string; long?: boolean; values?: Record<string, string> }[] = [
  // `values` maps a stored enum value to the word the app shows, so the history
  // reads "changed status from To Do to In Progress", not the raw "todo"/
  // "inprogress" keys. The from_status/to_status columns beside the sentence
  // keep the raw value (set in store.ts) for the charts that read them.
  { key: 'status', label: 'status', values: STATUS_LABELS as Record<string, string> },
  { key: 'priority', label: 'priority' },
  { key: 'assignee', label: 'assignee' },
  { key: 'responsible', label: 'responsible' },
  /*
   * SIZE JOINED THIS LIST ON 15 AUGUST 2026, when sizes stopped being three
   * loose words and started carrying numbers. Before that a size change was a
   * private opinion about one card and leaving it out of the history was
   * defensible; now it changes what a fortnight is said to contain, and a
   * sprint whose total moved with nothing in the history to say why is exactly
   * the kind of unexplained number that makes a team distrust a chart.
   */
  { key: 'size', label: 'size' },
  { key: 'epic', label: 'epic' },
  { key: 'title', label: 'title', long: true },
  { key: 'blocked', label: 'blocked reason', long: true },
  { key: 'summary', label: 'plain-English summary', long: true },
  { key: 'stands', label: 'update', long: true },
  { key: 'description', label: 'technical notes', long: true },
];

export function describeChange(before: Issue, patch: Partial<Issue>, who: string): Activity[] {
  const out: Activity[] = [];
  for (const f of TRACKED) {
    if (!(f.key in patch)) continue;
    const fromRaw = (before[f.key] ?? '') as string;
    const toRaw = (patch[f.key] ?? '') as string;
    if (fromRaw === toRaw) continue;
    const from = f.values?.[fromRaw] ?? fromRaw;
    const to = f.values?.[toRaw] ?? toRaw;
    let what: string;
    /*
     * "the" for all three, not "a" for one of them. It read "added a technical
     * notes" — an article in front of a plural — and would have said "added a
     * "where this stands"" too. Naming the field the same way whether it was
     * added, edited or cleared also makes the three read as one sentence shape,
     * which is what somebody scanning a history is actually doing.
     */
    if (f.key === 'stands')
      // The status log is the card's running feed of updates now (the merge of
      // 4 Oct 2026), so a change to it reads as posting or editing an update —
      // not as editing a field called "where this stands".
      what = to ? (from ? 'edited an update' : 'posted an update') : 'removed an update';
    else if (f.long) what = to ? (from ? `edited the ${f.label}` : `added the ${f.label}`) : `cleared the ${f.label}`;
    else if (!to) what = `cleared the ${f.label}`;
    else if (!from) what = `set ${f.label} to ${to}`;
    else what = `changed ${f.label} from ${from} to ${to}`;
    // The old wording, on the long fields only, so an accident is readable
    // rather than final — see Activity.was. Short fields already print both
    // halves in the sentence above, and `undefined` cannot be written to the
    // database at all, so it is left off rather than set empty.
    const was = f.long && from ? from : undefined;
    out.push({ id: uid(), who, at: Date.now(), what, ...(was ? { was } : {}) });
  }
  return out;
}

/**
 * WHETHER AN ENTRY BELONGS TO A PROJECT AT ALL.
 *
 * Every entry used to be stamped with whichever board happened to be open. For
 * a sign-in that is meaningless — you sign in to the WORKSPACE, not to a
 * project, and which board was selected at that instant says nothing about it.
 * Adding somebody is the same: they are added to the workspace.
 *
 * Worse, the stamp was read from the board's details, which are not loaded yet
 * at the moment somebody signs in — so all twelve sign-ins on this board were
 * labelled "FD", the app's own starting placeholder, naming a project that has
 * never existed.
 */
/* Support acts on the workspace, not on one project — a visit reaches all of
   them and adding somebody is not about any one board. Stamping a project name
   on it would name a board the act had nothing to do with. */
const WORKSPACE_KINDS: AuditKind[] = ['signin', 'invite', 'access', 'role', 'support', 'session', 'settings'];

export const stampsProject = (kind: AuditKind): boolean => !WORKSPACE_KINDS.includes(kind);

export const AUDIT_LABEL: Record<AuditKind, string> = {
  signin: 'Signed in',
  /*
   * "Session ended", not "Signed out" — one of the four is not a sign-out at
   * all. A session that ages out ends without anybody doing anything, and a
   * badge reading "Signed out" above a line saying nobody signed out is the
   * same contradiction `issue` was renamed to avoid.
   */
  session: 'Session ended',
  /*
   * ITS OWN BADGE, ASKED FOR 18 AUGUST 2026. An invitation was recorded as an
   * "Access change" — true, and it read the same as being given a role or
   * being taken off the list. Vinnie: "when someone is invited the log in
   * History should say Invited as well, and then when they log in it shows
   * them as being logged in."
   *
   * Those are two events about one person, minutes apart, and the pair is the
   * thing worth reading. Older entries stay as they were written — a badge is
   * not worth rewriting history for.
   */
  invite: 'Invited',
  role: 'Role change',
  access: 'Access change',
  project: 'Project change',
  // Not "Issue deleted": the same kind carries deletions, restores and changes
  // made to many issues at once, and a badge reading "deleted" above a line
  // saying "restored #8" contradicted itself. The line says what happened.
  issue: 'Issue change',
  comment: 'Comment removed',
  refused: 'Turned away',
  // Says WHO rather than what. The sentence beside it already says what was
  // done; the fact that FlightDeck did it is the part worth a badge.
  support: 'FlightDeck',
  // A workspace-wide preference an admin set — the currency, so far. The
  // sentence beside it says which setting and what it became.
  settings: 'Setting change',
};

/**
 * ONE ACT, RECORDED ONCE.
 *
 * Clearing a blocker on twenty issues wrote twenty lines across twenty cards
 * and nothing anywhere saying one person did one thing at one moment. That is
 * exactly what made 5 August hard to reconstruct: the individual changes were
 * all there, and the FACT that they were a single press was nowhere.
 *
 * The references are named, all of them. A count alone — "cleared 20 blockers"
 * — tells you the size of what happened and not which work it touched, which
 * is the only part that helps you put it right.
 */
export function describeBulk(what: string, issues: Pick<Issue, 'ref'>[]): string {
  const refs = issues.map((i) => i.ref);
  return `${what} on ${refs.length} issue${refs.length === 1 ? '' : 's'} (${refs.join(', ')})`;
}

/**
 * A comment being taken down, said so an admin can see it without opening the
 * card it was on.
 *
 * The words themselves are NOT copied here. Removing a comment hides it rather
 * than deleting it — the text is still on the issue — so a second copy would be
 * spreading someone's words further as a side effect of taking them down.
 */
export function describeCommentRemoval(
  issueRef: string,
  author: string
): string {
  return `removed a comment by ${author} on ${issueRef}`;
}

/**
 * How an edit is described in the trail — the words were changed, not added or
 * withdrawn. `mine` is whether the editor wrote the entry themselves: tidying
 * your own words reads differently from an admin changing somebody else's, and
 * the trail should keep the difference. See [[project-status-update-purpose]] —
 * an update is the running narration, and "awaiting feedback" becoming "feedback
 * received" is an edit worth recording.
 */
export function describeCommentEdit(issueRef: string, author: string, mine: boolean): string {
  return mine ? `edited their comment on ${issueRef}` : `edited a comment by ${author} on ${issueRef}`;
}

export function describeUpdateEdit(issueRef: string, author: string, mine: boolean): string {
  return mine ? `edited their update on ${issueRef}` : `edited an update by ${author} on ${issueRef}`;
}

/**
 * An update or note being taken off a card. Like a comment removal, this is
 * recorded so an admin can see it without opening the card — and a status MOVE is
 * never removable, so this only ever describes a note or a plain update. `mine`
 * keeps the difference between withdrawing your own and an admin taking down
 * someone else's; `quiet` names it truthfully — a Note was a quiet entry, an
 * Update a broadcast one, and the trail says which was taken down ("a note" vs
 * "an update").
 */
export function describeUpdateRemoval(
  issueRef: string,
  author: string,
  mine: boolean,
  quiet: boolean
): string {
  const noun = quiet ? 'note' : 'update';
  const withArticle = quiet ? 'a note' : 'an update';
  return mine
    ? `removed their ${noun} on ${issueRef}`
    : `removed ${withArticle} by ${author} on ${issueRef}`;
}

/**
 * How a deleted issue is described, and how it is kept.
 *
 * The description names the reference AND the title, because a reference alone
 * is unreadable once the card it pointed at is gone — "deleted #206" tells you
 * nothing about what was lost.
 */
export function describeDeletion(issue: Pick<Issue, 'ref' | 'title'>): string {
  const title = issue.title.trim();
  return title ? `deleted ${issue.ref} — "${title}"` : `deleted ${issue.ref}`;
}

/**
 * The issue, frozen as text so it can be written back exactly as it was.
 *
 * Stored as a string rather than as a nested object on purpose: the trail is
 * append-only and must never be edited, and a plain string cannot be partially
 * rewritten by a later change to what an issue looks like.
 */
export const snapshotOf = (issue: Issue): string => JSON.stringify(issue);

/** The issue again, or null if the record is unreadable. */
export function issueFromSnapshot(snapshot: string | undefined): Issue | null {
  if (!snapshot) return null;
  try {
    const parsed = JSON.parse(snapshot) as Issue;
    // An id and a reference are the two things a restore cannot invent.
    return parsed && parsed.id && parsed.ref ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * One sign-in record per browser session, not one per token refresh.
 *
 * Firebase re-announces the signed-in user on every page load and whenever it
 * renews a token, which is roughly hourly. Recording each of those would bury
 * the genuine sign-ins under noise and make the trail useless — the failure
 * mode that makes people stop reading a log.
 */
const SESSION_KEY = 'flightdeck-session-logged';

export function alreadyLoggedThisSession(email: string): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === email;
  } catch {
    return false; // private browsing — a duplicate entry beats a missing one
  }
}

/**
 * A refusal is recorded once per session too, and separately from a sign-in.
 *
 * The denial handler fires on EVERY listener that is refused — projects, the
 * access list, the roles, the audit itself — so a single turned-away visit
 * would otherwise write half a dozen identical lines and bury the trail under
 * the very event it exists to surface.
 */
const REFUSED_KEY = 'flightdeck-refusal-logged';

export function alreadyRefusedThisSession(email: string): boolean {
  try {
    return sessionStorage.getItem(REFUSED_KEY) === email;
  } catch {
    return false;
  }
}

export function markRefusalLogged(email: string): void {
  try {
    sessionStorage.setItem(REFUSED_KEY, email);
  } catch {
    /* nothing to do */
  }
}

export function markSessionLogged(email: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, email);
  } catch {
    /* nothing to do */
  }
}

/**
 * Where they were when they signed in, so a trail spanning copies stays readable.
 *
 * ── IT CALLED THE TEST COPY "THE LIVE BOARD" FOR AS LONG AS IT EXISTED ─────
 *
 * This used to decide by looking for `--` in the hostname. Only a Firebase
 * PREVIEW channel has that; the test copy's own addresses —
 * `flightdeck-uat-584f7.web.app` and `uat.flightdeck.build` — do not. So every
 * test-copy sign-in was labelled `the live board`, on the Devices screen and,
 * far worse, in the audit trail, which is append-only and cannot be corrected.
 * A witness found it on 26 August 2026 while checking #289.
 *
 * WHICH IS THE EXACT OPPOSITE OF THE PURPOSE ON THE LINE ABOVE. A trail
 * spanning copies is readable only if it says which copy; one that files test
 * activity under the live board is worse than one that says nothing, because
 * it will be believed.
 *
 * `currentEnv()` IS THE ANSWER AND WAS ALL ALONG. It already decides this, and
 * decides it the safe way round — a real hostname AND a build stamp that
 * agrees, or else "uat" — so a test copy can never talk its way into calling
 * itself live. Two places deciding the same thing by different rules is what
 * produced this; there is one now.
 *
 * THE ROWS ALREADY WRITTEN STAY WRONG. Migration rules make audit entries
 * append-only on purpose, including for admins, so they cannot be corrected
 * and should not be. Anything dated before 26 August 2026 saying "the live
 * board" may have been the test copy.
 */
export function whereFrom(): string {
  if (typeof window === 'undefined') return '';
  switch (currentEnv()) {
    case 'dev':
      return 'a development machine';
    case 'uat':
      return 'the test copy';
    default:
      return 'the live board';
  }
}
